import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Zap, Shield, Unlink, Eye, EyeOff, ExternalLink, Download, X as XIcon, RefreshCw, RotateCw } from "lucide-react";
import { Header } from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import {
  getTradingConnection,
  connectTradier,
  disconnectTradier,
  setTradeMode,
  getTradingOrders,
  importPreview,
  importCommit,
  orderStatus,
  cancelOrder,
  orderSellToOpen,
  orderBuyToClose,
  orderRoll,
} from "../lib/api";
import { STATUS_COLORS, isOpenStatus, pollOrderStatus } from "../lib/orderStatus";
import { STRATEGY_BY_KEY } from "../lib/strategies";

export default function TradingSettingsPage() {
  const { refresh } = useAuth();
  const [conn, setConn] = useState(null);
  const [orders, setOrders] = useState([]);
  const [token, setToken] = useState("");
  const [env, setEnv] = useState("sandbox");
  const [showTok, setShowTok] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {importable, duplicates} or null
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [cancelling, setCancelling] = useState({});
  const [amend, setAmend] = useState(null); // {order, contracts, limit_price} being edited
  const [resubmitting, setResubmitting] = useState(false);
  const pollTimer = useRef(null);

  // Auto-refresh any open live orders every 5s. Cheap because we hit the local
  // /orders endpoint (which already caches the latest known status), and the
  // /orders/{id}/status endpoint only proxies Tradier for orders whose local
  // status isn't terminal.
  const refreshStatuses = useCallback(async (currentOrders) => {
    const openLive = currentOrders.filter((o) => o.mode === "live" && isOpenStatus(o.status));
    if (openLive.length === 0) return;
    try {
      const updated = await Promise.all(openLive.map((o) => orderStatus(o.id).catch(() => null)));
      setOrders((prev) => prev.map((o) => {
        const s = updated.find((x) => x && x.order_id === o.id);
        return s ? { ...o, status: s.status, avg_fill_price: s.avg_fill_price, exec_quantity: s.exec_quantity } : o;
      }));
    } catch { /* swallow — next tick will retry */ }
  }, []);

  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (!conn?.connected) return;
    pollTimer.current = setInterval(() => refreshStatuses(orders), 5000);
    return () => pollTimer.current && clearInterval(pollTimer.current);
  }, [conn?.connected, orders, refreshStatuses]);

  const doCancel = async (o) => {
    if (!confirm(`Cancel Tradier order for ${o.symbol}?`)) return;
    setCancelling((prev) => ({ ...prev, [o.id]: true }));
    try {
      await cancelOrder(o.id);
      toast.success("Cancel submitted");
      // Optimistic update + refresh
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status: "canceled" } : x)));
      const fresh = await getTradingOrders();
      setOrders(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling((prev) => ({ ...prev, [o.id]: false }));
    }
  };

  const openAmend = (o) => {
    setAmend({
      order: o,
      contracts: o.contracts || 1,
      // For rolls Tradier's price was stored as an absolute magnitude with sign
      // implied by order_type=credit/debit — recover the signed net_credit.
      limit_price: o.action === "roll"
        ? (o.order_type === "debit" ? -Math.abs(Number(o.price || 0)) : Math.abs(Number(o.price || 0)))
        : (o.price ?? ""),
    });
  };

  const doResubmit = async () => {
    if (!amend) return;
    const { order: o, contracts, limit_price } = amend;
    setResubmitting(true);
    try {
      const price = limit_price === "" || limit_price === null ? null : Number(limit_price);
      let r;
      if (o.action === "sell_to_open") {
        r = await orderSellToOpen({
          symbol: o.symbol, strike: o.strike, expiration: o.expiration,
          contracts: Number(contracts), limit_price: price,
        });
      } else if (o.action === "buy_to_close") {
        if (!o.position_id) throw new Error("Original position id missing — can't retry buy-to-close");
        r = await orderBuyToClose({ position_id: o.position_id, limit_price: price });
      } else if (o.action === "roll") {
        if (!o.position_id) throw new Error("Original position id missing — can't retry roll");
        r = await orderRoll({
          position_id: o.position_id,
          new_strike: o.to_strike, new_expiration: o.to_expiration,
          limit_price: price,
        });
      } else {
        throw new Error(`Unknown action ${o.action}`);
      }
      toast.success(
        r.mode === "live"
          ? `Resubmitted · Tradier order ${r.tradier_order_id || "queued"}`
          : "Resubmitted (paper)"
      );
      if (r.mode === "live" && r.order_id) {
        pollOrderStatus({
          orderId: r.order_id,
          onUpdate: (s) => {
            const term = String(s.status).toLowerCase();
            if (["filled", "canceled", "cancelled", "rejected", "expired"].includes(term)) {
              toast[term === "filled" ? "success" : "warning"](`Order ${term}${s.avg_fill_price ? ` @ $${Number(s.avg_fill_price).toFixed(2)}` : ""}`);
              getTradingOrders().then(setOrders).catch(() => {});
            }
          },
        });
      }
      setAmend(null);
      const fresh = await getTradingOrders();
      setOrders(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Resubmit failed");
    } finally { setResubmitting(false); }
  };

  // Terminal-but-failed statuses that make sense to retry.
  const RETRYABLE = new Set(["rejected", "canceled", "cancelled", "expired", "error"]);

  const load = useCallback(async () => {
    try {
      const c = await getTradingConnection();
      setConn(c);
      if (c.connected) {
        setOrders(await getTradingOrders());
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trading status");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doConnect = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try {
      const r = await connectTradier({ token: token.trim(), env });
      toast.success(`Connected · ${r.env} · ${r.account_masked}`);
      setToken("");
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    } finally { setBusy(false); }
  };

  const doDisconnect = async () => {
    if (!confirm("Disconnect Tradier? Any linked token will be wiped and mode reset to paper.")) return;
    setBusy(true);
    try {
      await disconnectTradier();
      toast.success("Tradier disconnected");
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const flipMode = async (next) => {
    setBusy(true);
    try {
      await setTradeMode(next);
      toast.success(`Trade mode: ${next.toUpperCase()}`);
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mode change failed");
    } finally { setBusy(false); }
  };

  const openImport = async () => {
    setBusy(true);
    try {
      const p = await importPreview();
      setPreview(p);
      setSelected(new Set((p.importable || []).map((x) => x.occ)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import preview failed");
    } finally { setBusy(false); }
  };

  const toggleSelected = (occ) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(occ)) next.delete(occ); else next.add(occ);
      return next;
    });
  };

  const commitImport = async () => {
    setImporting(true);
    try {
      const r = await importCommit(Array.from(selected));
      toast.success(`Imported ${r.imported.length}, skipped ${r.skipped.length}`);
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  if (!conn) {
    return (
      <div className="App flex flex-col h-screen">
        <Header />
        <main className="flex-1 overflow-y-auto max-w-4xl w-full mx-auto px-6 py-6">
          <p className="text-xs text-[#71717A]">Loading trading status…</p>
        </main>
      </div>
    );
  }

  // Invitation-only gate
  if (!conn.trading_enabled) {
    return (
      <div className="App flex flex-col h-screen">
        <Header />
        <main className="flex-1 overflow-y-auto max-w-4xl w-full mx-auto px-6 py-6 space-y-4">
          <BackRow />
          <div className="panel p-6 space-y-3" data-testid="trading-gated">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#FF9F0A]" />
              <div className="label-cap text-[#FF9F0A]">Invitation Only</div>
            </div>
            <h1 className="text-xl font-bold">Live Trading is not enabled for your account.</h1>
            <p className="text-xs text-[#A1A1AA] leading-relaxed max-w-lg">
              This feature routes real orders to Tradier. It&rsquo;s rolled out one user at a time.
              Ask an admin to grant you access, then reload this page.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-4xl w-full mx-auto px-6 py-6 space-y-4">
        <BackRow />

        <section className="panel" data-testid="trading-connection-panel">
          <div className="panel-h flex items-center justify-between">
            <div className="label-cap">Tradier Connection</div>
            {conn.connected && (
              <span className="text-[10px] font-mono text-[#00E676]">● CONNECTED</span>
            )}
          </div>
          <div className="p-4 space-y-4">
            {!conn.connected ? (
              <>
                <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                  Paste a Tradier Personal Access Token to link your brokerage account.
                  {" "}
                  <a href="https://documentation.tradier.com/brokerage-api/overview/access-tokens" target="_blank" rel="noreferrer"
                     className="text-[#BF5AF2] hover:underline inline-flex items-center gap-1">
                    Where to find it <ExternalLink size={10} />
                  </a>
                </p>
                <div>
                  <div className="label-cap mb-1">Environment</div>
                  <div className="grid grid-cols-2 gap-2">
                    {["sandbox", "production"].map((e) => (
                      <button
                        key={e}
                        onClick={() => setEnv(e)}
                        data-testid={`trading-env-${e}`}
                        className={`py-2 text-xs font-semibold uppercase tracking-widest border transition-colors ${
                          env === e
                            ? e === "sandbox"
                              ? "border-[#00E676] bg-[rgba(0,230,118,0.08)] text-[#00E676]"
                              : "border-[#FF3B30] bg-[rgba(255,59,48,0.08)] text-[#FF3B30]"
                            : "border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]"
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#71717A] mt-1">
                    Start with sandbox — sandbox tokens only work against Tradier&rsquo;s paper API.
                  </p>
                </div>
                <div>
                  <div className="label-cap mb-1">Personal Access Token</div>
                  <div className="relative">
                    <input
                      type={showTok ? "text" : "password"}
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="paste token…"
                      data-testid="trading-token-input"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 pr-9 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTok((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                    >
                      {showTok ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                  <p className="text-[10px] text-[#71717A] mt-1">
                    Token is encrypted at rest and never sent to any third party. Only the last 4 digits of your account number are shown to admins.
                  </p>
                </div>
                <button
                  onClick={doConnect}
                  disabled={busy || !token.trim()}
                  data-testid="trading-connect-btn"
                  className="btn-accent w-full py-2 text-xs disabled:opacity-40"
                >
                  {busy ? "Connecting…" : `Connect Tradier (${env})`}
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="label-cap mb-1">Environment</div>
                    <div className={`font-mono font-semibold ${conn.env === "sandbox" ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                      {conn.env?.toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <div className="label-cap mb-1">Account</div>
                    <div className="font-mono">{conn.account_masked || "—"}</div>
                  </div>
                </div>
                <button
                  onClick={doDisconnect}
                  disabled={busy}
                  data-testid="trading-disconnect-btn"
                  className="btn-outline w-full py-2 text-xs text-[#FF3B30] border-[#FF3B30] hover:bg-[rgba(255,59,48,0.08)] flex items-center justify-center gap-1.5 disabled:opacity-40"
                >
                  <Unlink size={13} /> Disconnect Tradier
                </button>
              </div>
            )}
          </div>
        </section>

        {conn.connected && (
          <section className="panel" data-testid="trading-mode-panel">
            <div className="panel-h">
              <div className="label-cap">App Mode · does ROLL.OPS actually hit Tradier?</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="border border-[#3F3F46] bg-[rgba(255,214,10,0.05)] p-3 text-[11px] leading-relaxed">
                <p className="font-bold text-[#FFD60A] mb-1 uppercase tracking-wider text-[10px]">Two independent things — don&rsquo;t mix them up</p>
                <p className="text-[#A1A1AA]">
                  <span className="text-white font-semibold">Tradier environment</span> (chosen when you connected): you&rsquo;re on{" "}
                  <span className={`font-mono font-bold ${conn.env === "sandbox" ? "text-[#00E676]" : "text-[#FF3B30]"}`}>{conn.env?.toUpperCase()}</span>
                  {conn.env === "sandbox"
                    ? " — virtual money, but real Tradier orders that show up in the Tradier sandbox dashboard."
                    : " — real brokerage account, real money."}
                </p>
                <p className="text-[#A1A1AA] mt-1">
                  <span className="text-white font-semibold">App mode</span> (toggle below): whether ROLL.OPS actually calls Tradier.
                  <span className="text-[#00E676]"> SIMULATE</span> = we log a fake fill and stop.
                  <span className="text-[#FF3B30]"> SUBMIT</span> = we send the order to Tradier.
                </p>
                <p className="text-[#71717A] mt-2 text-[10px]">
                  To see orders appear in your Tradier {conn.env} dashboard: flip to <span className="text-[#FF3B30] font-bold">SUBMIT</span>.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => flipMode("paper")}
                  disabled={busy || conn.trade_mode === "paper"}
                  data-testid="trading-mode-paper"
                  className={`py-3 text-xs font-bold uppercase tracking-widest border transition-colors ${
                    conn.trade_mode === "paper"
                      ? "border-[#00E676] bg-[rgba(0,230,118,0.12)] text-[#00E676]"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-[#00E676]"
                  }`}
                >
                  Simulate
                  <div className="text-[9px] font-normal text-[#71717A] mt-0.5 tracking-normal normal-case">Nothing hits Tradier</div>
                </button>
                <button
                  onClick={() => flipMode("live")}
                  disabled={busy || conn.trade_mode === "live"}
                  data-testid="trading-mode-live"
                  className={`py-3 text-xs font-bold uppercase tracking-widest border transition-colors flex flex-col items-center justify-center ${
                    conn.trade_mode === "live"
                      ? "border-[#FF3B30] bg-[rgba(255,59,48,0.15)] text-[#FF3B30]"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-[#FF3B30]"
                  }`}
                >
                  <span className="flex items-center gap-1.5"><Zap size={13} /> Submit · {conn.env?.toUpperCase()}</span>
                  <span className="text-[9px] font-normal text-[#71717A] mt-0.5 tracking-normal normal-case">
                    {conn.env === "sandbox" ? "Real Tradier order, virtual money" : "Real Tradier order, REAL money"}
                  </span>
                </button>
              </div>
            </div>
          </section>
        )}

        {conn.connected && (
          <section className="panel" data-testid="trading-import-panel">
            <div className="panel-h">
              <div className="label-cap">Import Positions</div>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                Pull every open option position from your Tradier account into the tracker
                (short calls, long calls, short puts, long puts — each auto-tagged to the
                right strategy panel). Duplicates (same symbol + strike + expiration) are
                always skipped — your existing entries are never touched.
              </p>
              <button
                onClick={openImport}
                disabled={busy}
                data-testid="trading-import-btn"
                className="btn-outline w-full py-2 text-xs flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <Download size={13} /> Scan Tradier for All Option Positions
              </button>
            </div>
          </section>
        )}

        {conn.connected && orders.length > 0 && (
          <section className="panel" data-testid="trading-orders-panel">
            <div className="panel-h">
              <div className="label-cap">Recent Orders ({orders.length})</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs grid-lines">
                <thead>
                  <tr className="text-left border-b border-[#3F3F46] label-cap">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Mode</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Symbol</th>
                    <th className="px-3 py-2">Details</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right w-20"></th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {orders.map((o) => {
                    const status = String(o.status || "unknown").toLowerCase();
                    const canCancel = o.mode === "live" && isOpenStatus(status);
                    return (
                      <tr key={o.id} className="border-b border-[#27272A]" data-testid={`order-row-${o.id}`}>
                        <td className="px-3 py-2 text-[#A1A1AA] whitespace-nowrap">{new Date(o.ts).toLocaleString()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${o.mode === "live" ? "text-[#FF3B30]" : "text-[#00E676]"}`}>
                            {o.mode === "live" ? "SUBMIT" : "SIMULATE"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            data-testid={`order-status-${o.id}`}
                            className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border"
                            style={{ color: STATUS_COLORS[status] || "#71717A", borderColor: STATUS_COLORS[status] || "#71717A" }}
                          >
                            {status}
                            {isOpenStatus(status) && o.mode === "live" && (
                              <RefreshCw size={9} className="inline ml-1 animate-spin" />
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 uppercase tracking-wider text-[10px]">{o.action.replace(/_/g, "-")}</td>
                        <td className="px-3 py-2 font-bold">{o.symbol}</td>
                        <td className="px-3 py-2 text-[#A1A1AA] text-[10px]">
                          {o.action === "roll"
                            ? `${o.from_strike}C ${o.from_expiration} → ${o.to_strike}C ${o.to_expiration}`
                            : `${o.strike}C · ${o.expiration}`}
                          {o.avg_fill_price != null && (
                            <span className="text-[#00E676] ml-2">@ ${Number(o.avg_fill_price).toFixed(2)}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{o.contracts}</td>
                        <td className="px-3 py-2 text-right">
                          {canCancel && (
                            <button
                              onClick={() => doCancel(o)}
                              disabled={!!cancelling[o.id]}
                              data-testid={`order-cancel-${o.id}`}
                              className="text-[10px] px-2 py-0.5 border border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] font-semibold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1"
                            >
                              <XIcon size={10} /> {cancelling[o.id] ? "…" : "Cancel"}
                            </button>
                          )}
                          {RETRYABLE.has(status) && (
                            <button
                              onClick={() => openAmend(o)}
                              data-testid={`order-amend-${o.id}`}
                              className="text-[10px] px-2 py-0.5 border border-[#FFD60A] text-[#FFD60A] hover:bg-[rgba(255,214,10,0.1)] font-semibold uppercase tracking-wider inline-flex items-center gap-1"
                              title="Amend price/quantity and resubmit"
                            >
                              <RotateCw size={10} /> Amend
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {amend && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !resubmitting && setAmend(null)}
            data-testid="order-amend-modal"
          >
            <div className="panel max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="panel-h flex items-center justify-between">
                <div className="label-cap flex items-center gap-2">
                  <RotateCw size={12} /> Amend &amp; Resubmit
                </div>
                <button onClick={() => setAmend(null)} disabled={resubmitting} className="text-[#71717A] hover:text-white">
                  <XIcon size={13} />
                </button>
              </div>
              <div className="p-4 space-y-3 text-xs">
                <p className="text-[#A1A1AA] leading-relaxed">
                  Original order was <span className="font-bold uppercase" style={{ color: STATUS_COLORS[String(amend.order.status).toLowerCase()] || "#71717A" }}>{amend.order.status}</span>.
                  Adjust the values below and resubmit — a new order will be created; the original stays in the log as a record.
                </p>
                <div className="border border-[#27272A] divide-y divide-[#27272A] font-mono">
                  <Row label="Action" value={String(amend.order.action).replace(/_/g, "-").toUpperCase()} />
                  <Row label="Symbol" value={amend.order.symbol} />
                  {amend.order.action === "roll" ? (
                    <>
                      <Row label="Buy to close" value={`$${amend.order.from_strike}C · ${amend.order.from_expiration}`} />
                      <Row label="Sell to open" value={`$${amend.order.to_strike}C · ${amend.order.to_expiration}`} />
                    </>
                  ) : (
                    <Row label="Contract" value={`$${amend.order.strike}C · ${amend.order.expiration}`} />
                  )}
                </div>
                {amend.order.action === "sell_to_open" && (
                  <div>
                    <div className="label-cap mb-1">Contracts</div>
                    <input
                      type="number" min="1" step="1"
                      value={amend.contracts}
                      onChange={(e) => setAmend((a) => ({ ...a, contracts: e.target.value }))}
                      data-testid="amend-contracts"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none"
                    />
                  </div>
                )}
                <div>
                  <div className="label-cap mb-1">
                    {amend.order.action === "roll" ? "Net credit (blank = market, negative = debit)" : "Limit price (blank = market)"}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] font-mono text-xs">$</span>
                    <input
                      type="number" step="0.01"
                      value={amend.limit_price ?? ""}
                      onChange={(e) => setAmend((a) => ({ ...a, limit_price: e.target.value }))}
                      placeholder={amend.order.action === "roll" ? "e.g. 0.85" : "e.g. 3.50"}
                      data-testid="amend-price"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] pl-7 pr-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none"
                    />
                  </div>
                  <p className="text-[10px] text-[#71717A] mt-1">
                    Original was {amend.order.price != null ? `$${Number(amend.order.price).toFixed(2)}` : "market"} ({amend.order.order_type}).
                  </p>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setAmend(null)}
                    disabled={resubmitting}
                    data-testid="amend-cancel"
                    className="btn-outline flex-1 py-2 disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={doResubmit}
                    disabled={resubmitting}
                    data-testid="amend-submit"
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest text-black disabled:opacity-40 ${
                      amend.order.mode === "live" ? "bg-[#FF3B30]" : "bg-[#00E676]"
                    }`}
                  >
                    {resubmitting ? "Submitting…" : `Resubmit (${(amend.order.mode || "paper").toUpperCase()})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {preview && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !importing && setPreview(null)}
            data-testid="import-preview-modal"
          >
            <div className="panel max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="panel-h flex items-center justify-between">
                <div className="label-cap">Import Preview · {preview.account_masked} · {preview.env}</div>
                <button onClick={() => setPreview(null)} disabled={importing} className="text-[#71717A] hover:text-white text-xs">✕</button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                {preview.importable.length === 0 && preview.duplicates.length === 0 && (
                  <p className="text-xs text-[#71717A] text-center py-8">
                    No open short-call positions found on your Tradier account.
                  </p>
                )}

                {preview.importable.length > 0 && (
                  <div>
                    <div className="label-cap mb-2 text-[#00E676]">
                      Available to Import ({preview.importable.length})
                    </div>
                    <table className="w-full text-xs grid-lines">
                      <thead>
                        <tr className="text-left border-b border-[#3F3F46] label-cap">
                          <th className="px-2 py-1.5 w-8"></th>
                          <th className="px-2 py-1.5">Strategy</th>
                          <th className="px-2 py-1.5">Symbol</th>
                          <th className="px-2 py-1.5">Strike</th>
                          <th className="px-2 py-1.5">Expiration</th>
                          <th className="px-2 py-1.5 text-right">Contracts</th>
                          <th className="px-2 py-1.5 text-right">Cost Basis/sh</th>
                          <th className="px-2 py-1.5 text-right">Market Bid</th>
                          <th className="px-2 py-1.5 text-right">Market Ask</th>
                          <th className="px-2 py-1.5 text-right">Market Mid</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {preview.importable.map((p) => {
                          const s = STRATEGY_BY_KEY[p.strategy] || { short: "??", label: p.strategy || "?", accent: "#71717A" };
                          const strikeSuffix = (p.option_type || "call") === "put" ? "P" : "C";
                          return (
                            <tr key={p.occ} className="border-b border-[#27272A]">
                              <td className="px-2 py-1.5">
                                <input
                                  type="checkbox"
                                  checked={selected.has(p.occ)}
                                  onChange={() => toggleSelected(p.occ)}
                                  data-testid={`import-check-${p.occ}`}
                                  className="accent-[#00E676]"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <span
                                  data-testid={`import-strategy-${p.occ}`}
                                  className="px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-widest"
                                  style={{ color: s.accent, borderColor: s.accent }}
                                  title={s.label}
                                >
                                  {s.short}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 font-bold">{p.symbol}</td>
                              <td className="px-2 py-1.5">${p.strike.toFixed(2)}{strikeSuffix}</td>
                              <td className="px-2 py-1.5">{p.expiration}</td>
                              <td className="px-2 py-1.5 text-right">{p.contracts}</td>
                              <td className="px-2 py-1.5 text-right">
                                {p.premium_received != null ? `$${p.premium_received.toFixed(2)}` : "—"}
                              </td>
                              <td className="px-2 py-1.5 text-right text-[#A1A1AA]">
                                {p.current_bid != null && p.current_bid > 0 ? `$${p.current_bid.toFixed(2)}` : "—"}
                              </td>
                              <td className="px-2 py-1.5 text-right text-[#A1A1AA]">
                                {p.current_ask != null && p.current_ask > 0 ? `$${p.current_ask.toFixed(2)}` : "—"}
                              </td>
                              <td className="px-2 py-1.5 text-right text-white">
                                {p.current_mid != null && p.current_mid > 0 ? `$${p.current_mid.toFixed(2)}` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview.duplicates.length > 0 && (
                  <div>
                    <div className="label-cap mb-2 text-[#71717A]">
                      Already in Tracker ({preview.duplicates.length}) — Skipped
                    </div>
                    <table className="w-full text-xs grid-lines">
                      <thead>
                        <tr className="text-left border-b border-[#3F3F46] label-cap">
                          <th className="px-2 py-1.5">Symbol</th>
                          <th className="px-2 py-1.5">Strike</th>
                          <th className="px-2 py-1.5">Expiration</th>
                          <th className="px-2 py-1.5 text-right">Contracts</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono text-[#71717A]">
                        {preview.duplicates.map((p) => (
                          <tr key={p.occ} className="border-b border-[#27272A]">
                            <td className="px-2 py-1.5">{p.symbol}</td>
                            <td className="px-2 py-1.5">${p.strike.toFixed(2)}{(p.option_type || "call") === "put" ? "P" : "C"}</td>
                            <td className="px-2 py-1.5">{p.expiration}</td>
                            <td className="px-2 py-1.5 text-right">{p.contracts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="border-t border-[#27272A] p-3 flex gap-2">
                <button
                  onClick={() => setPreview(null)}
                  disabled={importing}
                  data-testid="import-cancel"
                  className="btn-outline flex-1 py-2 text-xs disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={commitImport}
                  disabled={importing || selected.size === 0}
                  data-testid="import-commit"
                  className="btn-accent flex-1 py-2 text-xs disabled:opacity-40"
                >
                  {importing ? "Importing…" : `Import ${selected.size} position${selected.size === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const BackRow = () => (
  <div className="flex items-center justify-between">
    <div>
      <div className="label-cap">Settings</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1">Trading</h1>
      <p className="text-xs text-[#A1A1AA] mt-1 max-w-lg">
        Link your Tradier account to submit real covered-call orders. Paper mode works even without a connection.
      </p>
    </div>
    <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="trading-back-btn">
      <ArrowLeft size={14} /> Dashboard
    </Link>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between px-3 py-1.5">
    <span className="text-[#71717A]">{label}</span>
    <span className="text-white">{value}</span>
  </div>
);
